import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit'
import type { AppDispatch, RootState } from '@/store/types'
import type {
  IMessage,
  IMessageItem,
  IMessages,
  UpdateByIdProps,
  UpdateProps
} from './interfaces'

interface UpdateWithBreakProps {
  chatJid: string,
  messages: IMessage | IMessage[]
  breakId?: string
  insert?: 'after' | 'before'
}

const initialState: IMessages & {
  lastMessage: IMessage | null
  lastMessageBeforeOffline: IMessage | null
} = {
  messages: {},
  lastMessage: null,
  lastMessageBeforeOffline: null
}

interface IJidValue<T> {
  jid: string
  value: T
}

const createIfNotExist = (state: Draft<IMessages>, jid: string) => {
  if (!state.messages[jid]) {
    state.messages[jid] = {
      messages: {},
      isLoaded: false,
      isFullLoaded: false,
      replyMessage: null
    }
  }
  return state.messages[jid]
}

const messages = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    set(state, { payload }: { payload: IMessages }) {
      state.messages = payload.messages
    },
    add(state, { payload }: PayloadAction<{ jid: string, message: IMessage }>) {
      const { jid, message } = payload
      if (!jid) {
        return
      }
      const chatMessages = createIfNotExist(state, jid)
      chatMessages.messages[message.id] = message
    },
    update(state, { payload }: PayloadAction<UpdateProps | UpdateProps[]>) {
      const props = Array.isArray(payload) ? payload : [payload]
      props.forEach(prop => {
        const { jid, messages } = prop
        const chatMessages = createIfNotExist(state, jid)
        messages.forEach(message => {
          const oldMessage = chatMessages.messages[message.id]
          chatMessages.messages[message.id] = {
            ...oldMessage,
            ...message
          }
        })
      })
    },
    _updateById(state, { payload }: PayloadAction<UpdateByIdProps>) {
      const { id, chatJid, message } = payload
      const chat = state.messages[chatJid]
      if (!chat?.messages[id]) {
        return
      }
      const merged = {
        ...chat.messages[id],
        ...message
      }
      if (message.id != null && message.id !== id) {
        chat.messages[merged.id] = merged
      } else {
        chat.messages[id] = merged
      }
    },
    updateWithBreak(state, { payload }: PayloadAction<UpdateWithBreakProps>) {
      const { chatJid, messages } = payload
      const messagesArray = Array.isArray(messages) ? messages : [messages]
      const chatMessages = createIfNotExist(state, chatJid)
      messagesArray.forEach(message => {
        const oldMessage = chatMessages.messages[message.id]
        chatMessages.messages[message.id] = {
          ...oldMessage,
          ...message
        }
      })
    },
    setReply(state, { payload }: { payload: { jid: string, replyMessage: IMessage | null } }) {
      const { jid, replyMessage } = payload
      createIfNotExist(state, jid)
      if (!state.messages[jid]) {
        return
      }
      state.messages[jid].replyMessage = replyMessage
    },
    remove(state, { payload }: PayloadAction<{ jid: string, messageId: string }>) {
      const { jid, messageId } = payload
      delete state.messages[jid]?.messages[messageId]
    },
    removeMany(state, { payload }: PayloadAction<{ jid: string, messageIds: string[] }>) {
      const { jid, messageIds } = payload
      const chat = state.messages[jid]
      if (!chat) {
        return
      }
      messageIds.forEach(messageId => {
        delete chat.messages[messageId]
      })
    },
    clear(state, { payload: jid }: PayloadAction<string>) {
      if (!state.messages[jid]) {
        return
      }
      state.messages[jid].messages = {}
    },
    messagesWasLoaded(state, { payload }: { payload: IJidValue<boolean> | IJidValue<boolean>[] }) {
      const props = Array.isArray(payload) ? payload : [payload]
      props.forEach(({ jid, value }) => {
        createIfNotExist(state, jid)
        if (!state.messages[jid]) {
          return
        }
        state.messages[jid].isLoaded = value
      })
    },
    messagesWasFullLoaded(state, { payload }: { payload: { jid: string, value: boolean } }) {
      const { jid, value } = payload
      createIfNotExist(state, jid)
      if (!state.messages[jid]) {
        return
      }
      state.messages[jid].isFullLoaded = value
    },
    setLastMessageBeforeOffline(state, { payload }: { payload: IMessage | null }) {
      state.lastMessageBeforeOffline = payload
    },
    _readMessagesInChat(state, { payload }: PayloadAction<{ chatJid: string, userJid: string }>) {
      const { chatJid, userJid } = payload
      if (!state.messages[chatJid]) {
        return
      }
      const messages = state.messages[chatJid].messages
      Object.values(messages).forEach((message) => {
        if (message.from !== userJid && message.status !== 'displayed') {
          message.status = 'displayed'
        }
      })
    }
  }
})

export const {
  add,
  set,
  update,
  _updateById,
  updateWithBreak,
  setReply,
  clear,
  messagesWasLoaded,
  messagesWasFullLoaded,
  setLastMessageBeforeOffline,
  _readMessagesInChat,
  remove: _remove,
  removeMany: _removeMany
} = messages.actions

export const readMessagesInChat = (chatJid: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const userJid = getState().user.user?.$jid
    if (!userJid) {
      return
    }
    dispatch(_readMessagesInChat({ chatJid, userJid }))
  }



export const getAllMessages = (state: RootState) => state.messages.messages
export const getMessages = (jid: string) => (state: RootState): IMessageItem | undefined => state.messages.messages[jid]
export const getMessage = (chatJid: string, id: string) => (state: RootState): IMessage | undefined => state.messages.messages[chatJid]?.messages[id]
export const getReplyMessage = (jid: string) => (state: RootState) => state.messages.messages[jid]?.replyMessage
export const getLastMessage = (state: RootState) => state.messages.lastMessage
export default messages.reducer
